import React, { Fragment } from 'react';
import { Routes, Route } from 'react-router-dom';
import { auth } from './firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import store from './redux/config';
import { Provider } from 'react-redux';
import { match } from 'path-to-regexp';

import Login from './auth/Login';
//import LoadingSpinner from './utils/LoadingSpinner';
import { useSiteMap, useUserSiteMap } from './hooks/pages';
import NotAuthenticatedPublicPage from './views/Pages/NotAuthenticatedPublicPage';
import AuthenticatedPublicPage from './views/Pages/AuthenticatedPublicPage';
import AuthenticatedPrivatePage from './views/Pages/AuthenticatedPrivatePage';
import NotFound from './views/404';
import PageConfigurationError from './views/PageConfigurationError';
import FirestoreManager from './scaffolding/Firestore';
import { consoleLog } from './utils/Logger';
import useActionSequences from './hooks/actions';
import LoadingSpinner from './utils/LoadingSpinner';

const Scaffolding = React.lazy(() => import('./scaffolding'));
const Signing = React.lazy(() => import('./areas/signing'));

//import { loadBundle } from 'firebase/firestore';

const App = () => {
  const [user, loading] = useAuthState(auth);

  const { siteMap } = useSiteMap();

  /*useEffect(() => {
    async function getBundles() {
      // Download the bundle from the Firebase Hosting CDN:
      //const bundle = await fetch('/bundles/types');

      // If not using a CDN, download the bundle directly:
      const bundle = await fetch(
        'https://us-central1-csg-introducer.cloudfunctions.net/ext-firestore-bundle-builder-serve/types'
      );
      await loadBundle(bundle);
      consoleLog('types.bundle', bundle);
    }
    getBundles();
  }, []);*/

  consoleLog('App.sitemap', { siteMap, loading, user });

  if (loading) {
    return null;
    /*return (
      <div className="h-full">
        <LoadingSpinner footertext="Loading..." />
      </div>
    );*/
  }

  return (
    <div className="h-full">
      <Provider store={store}>
        <FirestoreManager />
        <AppRoutingLayout siteMap={siteMap} user={user} />
      </Provider>
    </div>
  );
};

const AppRoutingLayout = ({ siteMap, user }) => {
  /* the action sequencer that will be used on the page */

  const sequencer = useActionSequences();

  return (
    <Fragment>
      {!user ? (
        <Routes>
          <Route path="/admin" element={<Login />} />
          {siteMap
            ?.filter(p => p.publicAccess)
            ?.map((siteMapPage, index) => (
              <Route
                key={index}
                path={siteMapPage.path}
                element={
                  <React.Suspense fallback={<>Please wait...</>}>
                    <NotAuthenticatedPublicPage
                      siteMapPage={siteMapPage}
                      sequencer={sequencer}
                    />
                  </React.Suspense>
                }
              />
            ))}
          {siteMap?.length > 0 &&
          !siteMap.find(p => p.publicAccess && p.path === '/') ? (
            <Route path="/" element={<Login siteMap={siteMap} />} />
          ) : null}
          <Route
            path="/sign"
            element={
              <Signing
                signatureId={window.location.pathname.replace('/sign', '')}
              />
            }
          />
          {siteMap?.length > 0 ? (
            <Route path="*" element={<NotFound />} />
          ) : null}
        </Routes>
      ) : (
        <AuthenticatedScaffoldingProxy
          siteMap={siteMap}
          sequencer={sequencer}
        />
      )}
    </Fragment>
  );
};

/*
  If we are navigating to a Page and not a model/contentType route, then we want to avoid
  loading scaffolding for performance, even though we are authenticated
  Pages can have the same paths but be intended for different roles
*/
const AuthenticatedScaffoldingProxy = ({ siteMap, sequencer }) => {
  const { userPages, ready } = useUserSiteMap({ siteMap });

  consoleLog('AuthenticatedScaffoldingProxy', {
    siteMap,
    sequencer,
    userPages
  });

  if (!ready || !userPages) {
    return <LoadingSpinner footertext="Getting things ready..." />;
  }

  let pageMatches = userPages.filter(p => {
    const isMatch = match(p.path, { decode: decodeURIComponent });
    return isMatch(window.location.pathname);
  });

  if (pageMatches.some(p => p.pageConfigurationError)) {
    return <PageConfigurationError />;
  }

  /* if the url matches a page that doesnt require a sidebar, return it, otherwise return standard scaffolding */
  return (
    <>
      {pageMatches.length === 1 && !pageMatches[0]?.showSidebar ? (
        <AuthenticatedPageRoutes
          siteMapPages={userPages}
          sequencer={sequencer}
        />
      ) : (
        <Scaffolding siteMap={userPages} sequencer={sequencer} />
      )}
    </>
  );
};

const AuthenticatedPageRoutes = ({ siteMapPages, sequencer }) => {
  consoleLog(`AuthenticatedPageRoutes`, siteMapPages);
  return (
    <Routes>
      {siteMapPages.map((siteMapPage, index) => (
        <Fragment key={index}>
          {consoleLog(`AuthenticatedScaffoldingProxy.Route:`, {
            path: siteMapPage.path,
            name: siteMapPage.name
          })}
          <Route
            key={index}
            path={siteMapPage.path}
            element={
              <React.Suspense fallback={<>Please wait...</>}>
                {siteMapPage?.publicAccess ? (
                  <AuthenticatedPublicPage
                    siteMapPage={siteMapPage}
                    sequencer={sequencer}
                  />
                ) : siteMapPage.pageConfigurationError ? (
                  <PageConfigurationError path={siteMapPage.path} />
                ) : (
                  <AuthenticatedPrivatePage
                    siteMapPage={siteMapPage}
                    sequencer={sequencer}
                  />
                )}
              </React.Suspense>
            }
          />
        </Fragment>
      ))}
    </Routes>
  );
};

export default App;
